(() => {
  const AMAZON_REVIEW_HOST = 'm.media-amazon.com';
  const REVIEW_CONTAINER_SELECTOR =
    '[class*="review" i], [id*="review" i], [data-testid*="review" i]';
  const AMAZON_RESIZE_SUFFIX = /\._[^./?]+(?=\.(?:avif|gif|jpe?g|png|webp)(?:[?#]|$))/i;
  const observedImages = new WeakSet();

  function reviewImageSource(image) {
    return (
      image.currentSrc ||
      image.getAttribute('src') ||
      image.getAttribute('data-src') ||
      image.getAttribute('data-lazy-src') ||
      ''
    );
  }

  function optimizedAmazonUrl(source) {
    try {
      // Some imported Air Reviews records contain several image URLs separated
      // by newlines in one field. The carousel only has one image element, so use
      // the first valid URL instead of allowing the browser to concatenate them.
      const firstSource = String(source || '').trim().split(/\s+/)[0];
      const url = new URL(firstSource, window.location.href);
      if (url.hostname !== AMAZON_REVIEW_HOST) return null;
      // 900px is sharp enough for the largest review card at 2x density while
      // avoiding multi-megabyte originals on mobile connections.
      const upgradedPath = url.pathname.replace(AMAZON_RESIZE_SUFFIX, '._SL900_');
      if (upgradedPath === url.pathname) return null;
      url.pathname = upgradedPath;
      return url.toString();
    } catch {
      return null;
    }
  }

  function upgradeReviewImage(image) {
    if (!(image instanceof HTMLImageElement)) return;

    const originalSource = reviewImageSource(image);
    const upgradedSource = optimizedAmazonUrl(originalSource);
    if (!upgradedSource || image.dataset.ohsioHdReview === 'true') return;

    image.dataset.ohsioHdReview = 'true';
    image.dataset.ohsioThumbnail = originalSource;
    image.loading = 'lazy';
    image.decoding = 'async';
    image.referrerPolicy = 'no-referrer';
    image.addEventListener(
      'error',
      () => {
        const fallback = image.dataset.ohsioThumbnail;
        if (fallback && image.src !== fallback) image.src = fallback;
      },
      { once: true },
    );
    image.src = upgradedSource;
    image.removeAttribute('srcset');
  }

  function prepareDeferredReviewImage(image) {
    if (!(image instanceof HTMLImageElement)) return false;

    const deferredAttribute = image.hasAttribute('data-src')
      ? 'data-src'
      : image.hasAttribute('data-lazy-src')
        ? 'data-lazy-src'
        : null;
    if (!deferredAttribute || image.hasAttribute('src')) return false;

    const thumbnail = image.getAttribute(deferredAttribute);
    const upgradedSource = optimizedAmazonUrl(thumbnail);
    if (!upgradedSource) return false;

    image.dataset.ohsioThumbnail = thumbnail;
    image.dataset.ohsioHdReview = 'true';
    image.setAttribute(deferredAttribute, upgradedSource);
    image.removeAttribute('data-srcset');
    image.loading = 'lazy';
    image.decoding = 'async';
    image.referrerPolicy = 'no-referrer';
    return true;
  }

  const reviewImageObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, observer) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              observer.unobserve(entry.target);
              upgradeReviewImage(entry.target);
            }
          },
          // Load shortly before the review section becomes visible without
          // competing with the product image and purchase controls.
          { rootMargin: '320px 0px' },
        )
      : null;

  function reviewContainers(root = document) {
    if (!root?.querySelectorAll) return [];

    const containers = [];
    if (root instanceof Element && root.matches(REVIEW_CONTAINER_SELECTOR)) containers.push(root);
    containers.push(...root.querySelectorAll(REVIEW_CONTAINER_SELECTOR));
    return containers;
  }

  function registerReviewImages(root = document) {
    const images = [];
    for (const container of reviewContainers(root)) {
      if (container instanceof HTMLImageElement) images.push(container);
      images.push(...container.querySelectorAll('img'));
    }

    for (const image of images) {
      if (observedImages.has(image)) continue;
      if (!optimizedAmazonUrl(reviewImageSource(image))) continue;
      observedImages.add(image);
      if (prepareDeferredReviewImage(image)) continue;
      if (reviewImageObserver) reviewImageObserver.observe(image);
      else upgradeReviewImage(image);
    }
  }

  function delayEmailPopup() {
    const style = document.createElement('style');
    style.id = 'ohsio-popup-delay-style';
    style.textContent = `
      html.ohsio-delay-email-popup [class*="window-popup" i],
      html.ohsio-delay-email-popup [class*="window_popup" i],
      html.ohsio-delay-email-popup [id*="window-popup" i],
      html.ohsio-delay-email-popup [id*="window_popup" i],
      html.ohsio-delay-email-popup [class*="bss" i][class*="modal" i],
      html.ohsio-delay-email-popup [id*="bss" i][id*="modal" i] {
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;
    document.head.append(style);
    document.documentElement.classList.add('ohsio-delay-email-popup');

    if (window.BSS_BP?.window_popup?.configs) {
      for (const config of window.BSS_BP.window_popup.configs) {
        config.delay_time_seconds = Math.max(Number(config.delay_time_seconds) || 0, 18);
        config.delay_time = Math.max(Number(config.delay_time) || 0, 18);
      }
    }

    window.setTimeout(() => {
      document.documentElement.classList.remove('ohsio-delay-email-popup');
    }, 18000);
  }

  delayEmailPopup();
  registerReviewImages();
  window.setTimeout(() => registerReviewImages(), 1500);

  function deferOffscreenReviewMedia(root) {
    const media = [];
    if (root instanceof HTMLImageElement || root instanceof HTMLIFrameElement || root instanceof HTMLVideoElement) {
      media.push(root);
    }
    if (root.querySelectorAll) {
      media.push(...root.querySelectorAll('img, iframe, video'));
    }

    for (const element of media) {
      if (element instanceof HTMLImageElement) {
        element.decoding = 'async';
        if (
          element.fetchPriority !== 'high' &&
          !element.closest('[data-testid="product-information-media"] slideshow-slide:first-child')
        ) {
          element.loading = 'lazy';
          element.fetchPriority = 'low';
        }
      } else if (element instanceof HTMLIFrameElement) {
        element.loading = 'lazy';
      } else if (
        element instanceof HTMLVideoElement &&
        !element.autoplay
      ) {
        element.preload = 'none';
      }
    }
  }

  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node instanceof Element) {
          registerReviewImages(node);
          for (const container of reviewContainers(node)) deferOffscreenReviewMedia(container);
        }
      }
    }
  });
  // Server-rendered media already carries the correct loading attributes.
  // Measuring every one of the homepage's images here forced hundreds of
  // synchronous layouts before the product grid could paint.
  const reviewMutationRoot = document.querySelector('#MainContent');
  if (reviewMutationRoot) mutationObserver.observe(reviewMutationRoot, { childList: true, subtree: true });
})();
